import {
  Button,
  Drawer,
  Form,
  Icons,
  notification,
  Space,
  Steps,
} from '@pankod/refine-antd'
import { useCustom, useCustomMutation } from '@pankod/refine-core'
import React, { useCallback, useState } from 'react'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { LandingPageCategory } from '@resources/lms-management/landing-page/types'
import { getGuruToken } from 'src/helpers/session'
import SelfEnrollmentBasicConfigFormFields from './SelfEnrollmentBasicConfigFormFields'
import SelfEnrollmentLandingPageFormFields, {
  BlueprintData,
} from './SelfEnrollmentLandingPageFormFields'
import {
  buildSelfEnrollmentConfigFormData,
  normalizeTotalWhitelistedUsers,
  prepareWhitelistUserCsvFile,
  SelfEnrollmentConfigFormValues,
} from './utils'

type TSelfEnrollmentConfigForm = SelfEnrollmentConfigFormValues

type TSelfEnrollmentLandingPage = {
  blueprintCourseId: string
  blueprint?: {
    courseId: string
    title: string
    description: string
    imageUrl: string
  }
  title?: string
  description?: string
  detail?: string
  badge?: string
  category?: LandingPageCategory
  provinceCodes?: string[]
  whitelistEmailDomains?: string[]
  totalWhitelistedUsers?: string | number
}

type TSelfEnrollmentConfigResponse = {
  data: {
    diklatLandingPage: TSelfEnrollmentLandingPage
    configuration: {
      quotaMaxProgram?: number
      quotaMaxKelas?: number
      isAutoApprove?: boolean
    }
  }
}

const STEP1_FIELDS: (keyof TSelfEnrollmentConfigForm)[] = [
  'quotaMaxProgram',
  'quotaMaxKelas',
  'isAutoApprove',
]

const defaultBlueprintData: BlueprintData = {
  imageUrl: '',
  title: '-',
  description: '-',
}

const getWhitelistUsersFileName = (
  contentDisposition: string | null,
  programId: string | number,
) => {
  const fileNameMatch = contentDisposition?.match(
    /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i,
  )
  const fileName = fileNameMatch?.[1]

  if (fileName) {
    return decodeURIComponent(fileName)
  }

  return `whitelisted_users_program_${programId}.csv`
}

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.URL.revokeObjectURL(url)
}

export const useSelfEnrollmentConfigDrawer = () => {
  const [showData, setShowData] = useState<{
    show: boolean
    programId: string | number
  }>({ show: false, programId: '' })

  const onClose = useCallback(() => {
    setShowData((state) => ({
      ...state,
      show: false,
    }))
  }, [])

  return {
    show: (programId: string | number) => {
      setShowData({ show: true, programId })
    },
    visible: !!showData.show,
    programId: showData.programId,
    onClose,
  }
}

type SelfEnrollmentConfigDrawerProps = {
  visible: boolean
  onClose: () => void
  programId: string | number
  onSubmitted?: () => void
}

const SelfEnrollmentConfigDrawer = ({
  visible,
  onClose,
  programId,
  onSubmitted,
}: SelfEnrollmentConfigDrawerProps) => {
  const [form] = Form.useForm<TSelfEnrollmentConfigForm>()
  const [currentStep, setCurrentStep] = useState(0)
  const [hasVisitedStep2, setHasVisitedStep2] = useState(false)
  const [blueprintData, setBlueprintData] =
    useState<BlueprintData>(defaultBlueprintData)
  const [initialBlueprintOption, setInitialBlueprintOption] = useState<
    { label: string; value: string | number } | undefined
  >(undefined)
  const [totalWhitelistedUsers, setTotalWhitelistedUsers] = useState(0)
  const [whitelistUserCsvFile, setWhitelistUserCsvFile] = useState<
    File | undefined
  >(undefined)
  const [whitelistUserCsvError, setWhitelistUserCsvError] = useState<
    string | undefined
  >(undefined)
  const [isDownloadingWhitelistUsers, setIsDownloadingWhitelistUsers] =
    useState(false)

  const { mutateAsync: doUpsertSelfEnrollmentConfig, isLoading } =
    useCustomMutation()
  const { isFetching: isFetchingInitialConfig } =
    useCustom<TSelfEnrollmentConfigResponse>({
      url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/self-enrollment/config`,
      method: 'get',
      queryOptions: {
        enabled: visible && !!programId,
        onSuccess: (response) => {
          const data = response?.data?.data
          const config = data?.configuration ?? {}
          const landingPage = data?.diklatLandingPage ?? {}

          form.setFieldsValue({
            quotaMaxProgram: config.quotaMaxProgram,
            quotaMaxKelas: config.quotaMaxKelas,
            isAutoApprove: config.isAutoApprove ?? false,
            blueprintCourseId:
              landingPage.blueprintCourseId ?? landingPage.blueprint?.courseId,
            title: landingPage.title,
            description: landingPage.description,
            detail: landingPage.detail,
            badge: landingPage.badge,
            category: landingPage.category,
            provinceCodes: landingPage.provinceCodes,
            whitelistEmailDomains: landingPage.whitelistEmailDomains,
          })
          setTotalWhitelistedUsers(
            normalizeTotalWhitelistedUsers(landingPage.totalWhitelistedUsers),
          )

          if (landingPage.blueprint) {
            setBlueprintData({
              imageUrl: landingPage.blueprint.imageUrl,
              title: landingPage.blueprint.title,
              description: landingPage.blueprint.description,
            })
            setInitialBlueprintOption({
              label: landingPage.blueprint.title,
              value: landingPage.blueprint.courseId,
            })
          }
        },
        onError: (error) => {
          showErrorNotification(
            error,
            'Terdapat gangguan saat mengambil konfigurasi pendaftaran mandiri',
          )
        },
      },
    })

  const handleNext = async () => {
    try {
      await form.validateFields(STEP1_FIELDS)
      setCurrentStep(1)
      setHasVisitedStep2(true)
    } catch {
      // validation errors shown inline
    }
  }

  const handleBack = () => {
    setCurrentStep(0)
  }

  const handleWhitelistUserCsvChange = async (file: File) => {
    const preparedCsv = await prepareWhitelistUserCsvFile(file)

    setWhitelistUserCsvFile(preparedCsv.file)
    setWhitelistUserCsvError(preparedCsv.error)
  }

  const handleDownloadWhitelistUsers = async () => {
    try {
      setIsDownloadingWhitelistUsers(true)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_LMS_API_URL}/v2/programs/${programId}/self-enrollment/whitelisted-user`,
        {
          headers: {
            Authorization: `Bearer ${getGuruToken()}`,
          },
        },
      )

      if (!response.ok) {
        throw new Error('Failed to download whitelist CSV')
      }

      downloadBlob(
        await response.blob(),
        getWhitelistUsersFileName(
          response.headers.get('content-disposition'),
          programId,
        ),
      )
    } catch {
      notification.error({
        message: 'Gagal mengunduh berkas CSV',
        description: 'Terdapat gangguan saat mengunduh daftar email whitelist',
      })
    } finally {
      setIsDownloadingWhitelistUsers(false)
    }
  }

  const onFinish = async (values: TSelfEnrollmentConfigForm) => {
    if (
      values.category === 'WHITELIST_USER' &&
      totalWhitelistedUsers === 0 &&
      !whitelistUserCsvFile
    ) {
      setWhitelistUserCsvError('Harus mengunggah berkas CSV')
      return
    }

    if (values.category === 'WHITELIST_USER' && whitelistUserCsvError) {
      return
    }

    await doUpsertSelfEnrollmentConfig(
      {
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/v2/programs/${programId}/self-enrollment/config`,
        method: 'post',
        dataProviderName: 'lms',
        values: buildSelfEnrollmentConfigFormData(values, whitelistUserCsvFile),
        config: {
          headers: {
            'content-type': 'multipart/form-data',
          },
        },
        successNotification: {
          message: 'Konfigurasi pendaftaran mandiri berhasil diperbarui',
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (error) =>
          showErrorNotification(
            error,
            'Terdapat gangguan saat menyimpan konfigurasi pendaftaran mandiri',
          ),
      },
      {
        onSuccess: () => {
          onSubmitted?.()
          onClose()
        },
      },
    )
  }

  return (
    <Drawer
      title="Kelola Pendaftaran Mandiri"
      visible={visible}
      onClose={onClose}
      destroyOnClose
      width="75%"
      maskClosable={false}
      afterVisibleChange={(isVisible) => {
        if (!isVisible) {
          form.resetFields()
          setCurrentStep(0)
          setHasVisitedStep2(false)
          setBlueprintData(defaultBlueprintData)
          setInitialBlueprintOption(undefined)
          setTotalWhitelistedUsers(0)
          setWhitelistUserCsvFile(undefined)
          setWhitelistUserCsvError(undefined)
          setIsDownloadingWhitelistUsers(false)
        }
      }}
      extra={
        <Space>
          {currentStep === 0 ? (
            <Button
              type="primary"
              onClick={handleNext}
              loading={isFetchingInitialConfig}
            >
              Lanjut <Icons.ArrowRightOutlined />
            </Button>
          ) : (
            <>
              <Button
                onClick={handleBack}
                icon={<Icons.ArrowLeftOutlined />}
                disabled={isLoading}
              >
                Kembali
              </Button>
              <Button
                icon={<Icons.SaveOutlined />}
                type="primary"
                loading={isLoading}
                onClick={() => form.submit()}
              >
                Simpan
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Steps current={currentStep}>
        <Steps.Step title="Konfigurasi Dasar" />
        <Steps.Step title="Konfigurasi Landing Page" />
      </Steps>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          isAutoApprove: false,
        }}
        onFinish={onFinish}
        validateMessages={{ required: '${label} harus diisi.' }}
      >
        <div
          className="mt-8"
          style={{ display: currentStep === 0 ? 'block' : 'none' }}
        >
          <SelfEnrollmentBasicConfigFormFields form={form} />
        </div>

        {hasVisitedStep2 && (
          <div
            className="mt-8"
            style={{ display: currentStep === 1 ? 'block' : 'none' }}
          >
            <SelfEnrollmentLandingPageFormFields
              form={form}
              visible={visible && currentStep === 1}
              blueprintData={blueprintData}
              initialBlueprintOption={initialBlueprintOption}
              totalWhitelistedUsers={totalWhitelistedUsers}
              whitelistUserCsvFile={whitelistUserCsvFile}
              whitelistUserCsvError={whitelistUserCsvError}
              isDownloadingWhitelistUsers={isDownloadingWhitelistUsers}
              onBlueprintSelect={(data) =>
                setBlueprintData(data || blueprintData)
              }
              onWhitelistUserCsvChange={handleWhitelistUserCsvChange}
              onDownloadWhitelistUsers={handleDownloadWhitelistUsers}
            />
          </div>
        )}
      </Form>
    </Drawer>
  )
}

export default SelfEnrollmentConfigDrawer
